"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NumberUtils = void 0;
/**
 * The `NumberUtils` provides utility functions related to numbers
 */
class NumberUtils {
    /**
     * Rounds a number with precision
     *
     * @param value The number to round
     * @param precision The precision to round
     * @returns The rounded number
     */
    static round(value, precision = 0) {
        const factor = Math.pow(10, precision);
        return Math.round(value * factor) / factor;
    }
    /**
     * Rounds a number up, if the difference between its rounded version and itself is greater than
     * Number.Epsilon. The number ((0.2 + 0.4) * 10) normally would be rounded up to 7 with this
     * method it stays at 6. The number (30 * 33.3) is rounded to 999 with this method. The number
     * ((3200.1 + 240022.2) * 1000) is rounded to 243222300 with this method 4567.001 is rounded up to
     * 4568 as expected. The calculation has been taken from the C++ boost library.
     * https://www.boost.org/doc/libs/1_68_0/libs/math/doc/html/math_toolkit/float_comparison.html
     *
     * @param value The floating point value.
     */
    static floatingPointRoundUp(value) {
        if (!this.isDefinedNumber(value)) {
            return value;
        }
        const roundedValue = Math.round(value);
        const delta = Math.abs(value - roundedValue);
        const absMinimum = Math.min(Math.abs(value), Math.abs(roundedValue));
        if (delta < Number.EPSILON || delta < Number.EPSILON * absMinimum) {
            return roundedValue;
        }
        return Math.ceil(value);
    }
    /**
     * Always rounds up with given precision
     *
     * @param value The number to round
     * @param precision The precision to round
     * @returns The number rounded up
     */
    static roundUp(value, precision = 0) {
        const factor = Math.pow(10, precision);
        if (Number.isInteger(value))
            return value;
        const returnValue = value * factor;
        const parts = returnValue.toString().split('.');
        if (parts.length === 1)
            return value;
        if (parts[1].startsWith('0'))
            return Math.floor(value * factor) / factor;
        return Math.floor(returnValue + 1) / factor;
    }
    /**
     * Converts any to number or undefined.
     *
     * This method is useful to avoid working with NaN values.
     */
    static toNumber(value) {
        if (value != null && value !== '') {
            const n = Number(value);
            if (NumberUtils.isDefinedNumber(n)) {
                return n;
            }
        }
        return undefined;
    }
    /**
     * Retrieves whether a given var is a defined number which can be used for calculations.
     * true:
     *    3
     *    '3'
     *    '3.3'
     *    0x0
     *    '0x0'
     *
     * false:
     *    null
     *    undefied
     *    NaN
     *    '3,3'
     *    ' '
     *    '3x'
     */
    static isDefinedNumber(value) {
        return typeof value === 'number' && !Number.isNaN(value) && Number.isFinite(value);
    }
    static isNullishOrDefinedNumber(n) {
        // allow `null` and `undefined`
        if (n === null || n === undefined)
            return true;
        return this.isDefinedNumber(n);
    }
}
exports.NumberUtils = NumberUtils;
//# sourceMappingURL=NumberUtils.js.map